// lib/utils/coveo/hooks/useProvidersByLocation.ts
import { useEffect, useState } from 'react';
import { useSitecoreContext } from '@sitecore-jss/sitecore-jss-nextjs';
import { loadFacetSetActions, Result, buildSearchEngine } from '@coveo/headless';
import { Settings } from '.generated/Feature.CW.model';
import { loadPaginationActions } from '@coveo/headless';

export type PageSettings = Settings.SiteSettings['fields'];

/**
 * Custom hook to fetch providers by location using Coveo Headless
 */
export function useProvidersByLocation(locationName: string) {
  const { sitecoreContext } = useSitecoreContext();
  const contextWithSettings = sitecoreContext?.siteSettings as PageSettings;

  const config = {
    searchHub: contextWithSettings?.providerSearchHub?.value || '',
    pipeline: contextWithSettings?.providerPipeline?.value || '',
    accessToken: contextWithSettings?.providerAccessToken?.value || '',
    organizationId: process.env.NEXT_PUBLIC_COVEO_ORG_ID || '',
    numberOfResults: 1000,
  };

  const [results, setResults] = useState<Result[]>([]);

  // helper for sorting
  const normalize = (val?: string) => (val || '').trim().toLowerCase();
  const sortResults = (results: Result[]) => {
    return results.slice().sort((a, b) => {
      const aAccepting = normalize(a?.raw?.cwacceptingnewpatients as string);
      const bAccepting = normalize(b?.raw?.cwacceptingnewpatients as string);

      const aPriority = aAccepting.startsWith('accepting') ? 0 : 1;
      const bPriority = bAccepting.startsWith('accepting') ? 0 : 1;

      if (aPriority !== bPriority) {
        return aPriority - bPriority;
      }

      const aFirst = normalize(a?.raw?.cwfirstname as string);
      const bFirst = normalize(b?.raw?.cwfirstname as string);

      return aFirst.localeCompare(bFirst);
    });
  };

  useEffect(() => {
    if (!locationName) return;

    const engine = buildSearchEngine({
      configuration: config,
    });

    // const { updatePagination } = loadPaginationActions(engine);
    // engine.dispatch(updatePagination({ numberOfResults: 2000 }));

    const { updateNumberOfResults } = loadPaginationActions(engine);
    engine.dispatch(updateNumberOfResults(1000)); // pick your desired page size
    const { registerFacet, toggleSelectFacetValue } = loadFacetSetActions(engine);
    const facetId = 'cwLocationFacet';

    // register and filter facet
    engine.dispatch(registerFacet({ facetId, field: 'cwlocationtitles' }));
    engine.dispatch(
      toggleSelectFacetValue({
        facetId,
        selection: { value: locationName.trim(), state: 'selected', numberOfResults: 0 },
      })
    );
    // Set number of results for this search
    engine.dispatch({
      type: 'search/updateQuery',
      payload: { numberOfResults: 1000 },
    });

    // Configure pagination for maximum results
    engine.dispatch({
      type: 'search/updatePagination',
      payload: { numberOfResults: 1000 },
    });

    // run first search
    engine.executeFirstSearch();

    // subscribe to state changes
    const unsubscribe = engine.subscribe(() => {
      const sorted = sortResults(engine.state.search.results);
      setResults(sorted);
    });

    return () => unsubscribe();
  }, [locationName]);

  return results;
}
